import { isBundleVersion } from 'platform/utilities/quick-submit-embed';

export const QS_EMBED_TAG = 'qs-embed-arp';

// The embed's own name for a rep. ARP serves no other, so this is also the
// embed's default. It is set anyway so the intent is plain.
export const QS_EMBED_USER_TYPE = 'partner';

export const SESSION_ERROR = Object.freeze({
  // Any identity trait vets-api could not resolve. Retrying cannot fix it.
  IDENTITY: 'identity',
  UNVERIFIED: 'unverified',
  UNAVAILABLE: 'unavailable',
  // The page still holds an older loader than the session asks for.
  STALE: 'stale',
  // The embed's token cannot be refreshed from the host, so a 401 in the
  // middle of an upload means the embed's session is over. An expired ARP
  // session goes to sign-in instead.
  SESSION_ENDED: 'session-ended',
  GENERIC: 'generic',
});

// Resolves `{ config, code }`, where config is the flat set of values the
// element reads off its dataset. Resolves null on an expired ARP session,
// while api.js sends the browser to sign-in.
export const createQuickSubmitSession = async api => {
  const response = await api.createQuickSubmitSession();
  if (!response) return null;

  const json = await response.json();
  const { apiBase, authPath, bundleVersion, theme } = json?.dataQsConfig ?? {};
  const code = json?.code;
  if (!apiBase || !authPath || !isBundleVersion(bundleVersion) || !code) {
    throw new Error('The QuickSubmit session response is missing fields');
  }
  return { config: { apiBase, authPath, bundleVersion, theme }, code };
};

// ARP's api wrapper throws the failed Response itself, not a parsed body, so
// this reads the body here. Anything that is not a Response is generic.
export const sessionErrorKind = async error => {
  if (!(error instanceof Response)) return SESSION_ERROR.GENERIC;

  let detail = '';
  try {
    const body = await error.json();
    const text = body?.errors?.[0]?.detail;
    detail = typeof text === 'string' ? text : '';
  } catch {
    // An empty or non-JSON body still has a status to go on.
  }

  if (error.status === 403) {
    return detail.includes('identity-verified')
      ? SESSION_ERROR.UNVERIFIED
      : SESSION_ERROR.UNAVAILABLE;
  }
  if (error.status === 422) return SESSION_ERROR.IDENTITY;
  return SESSION_ERROR.GENERIC;
};

const digits = value =>
  typeof value === 'string' || typeof value === 'number'
    ? `${value}`.replace(/\D/g, '')
    : '';

const text = value => (typeof value === 'string' ? value.trim() : '');

// The upload form takes only A-Z, hyphens and spaces, up to 50 characters. The
// embed shows a prefilled name read-only, so a name the form refuses can never
// be submitted. This makes an MPI name fit, as a rep typing it would have to:
// "José" becomes "Jose" and "O'Brien" becomes "OBrien". A letter with no A-Z
// form, such as "Ø", gives an empty name, so the rep types it in.
const NAME_MAX_LENGTH = 50;

const toFormName = value => {
  const folded = text(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[‘’ʼ]/g, '');
  if ([...folded].some(char => char.charCodeAt(0) > 0x7f)) return '';
  return folded
    .replace(/[^-\sa-zA-Z]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NAME_MAX_LENGTH)
    .trim();
};

// Maps the claimant route's body to the embed's claimant. The embed needs all
// four fields and rejects the mount on a gap or an unknown key, so this sends
// the claimant only when it is complete. The overview route sends only the
// last 4 SSN digits, so the full SSN comes from the prefill route.
// The claimantId does not ride here. This record exists only for a Veteran with
// a full prefill, and the drafts need the id for every claimant, so the embed
// gets it on its own (QuickSubmitEmbed's claimantId).
export const toEmbedClaimant = (raw, ssn) => {
  const data = raw?.data ?? raw;
  if (!data) return null;

  const firstName = toFormName(data.first_name);
  const lastName = toFormName(data.last_name);
  const fileNumber = digits(ssn);
  const address = data.address ?? {};
  const zipDigits = digits(address.zip || address.postal_code);
  // A ZIP+4 keeps its first 5. A ZIP is 5 or 9 digits, and nothing else.
  const zip =
    zipDigits.length === 5 || zipDigits.length === 9
      ? zipDigits.slice(0, 5)
      : '';

  if (!firstName || !lastName || fileNumber.length !== 9 || !zip) return null;
  return { firstName, lastName, fileNumber, zip };
};
